import { originTracingType } from './analyticsWebTypes';
import type { PackagedEvent } from './integration/types';
import urlParamExtractor from './urlUtils';

export default class OriginTracing {
	_originStore: any;
	private pendingCleanup: (() => void) | null = null;

	public static messageEvent = 'origin landed';

	constructor() {
		this._originStore = {};
	}

	handleOriginParameters = (
		originParamHandlerMapping: any,
		historyReplaceFn?: any,
	): {
		[key: string]: any;
	} => {
		// Collect all the parameter labels to be handled
		const originParamLabelList: string[] = Object.keys(originParamHandlerMapping);

		if (!(originParamLabelList.length > 0)) {
			throw new Error('Empty parameter mapping provided');
		}

		// Check if the parameter labels are valid. If any invalid labels are found throw an exception.
		const invalidParams = originParamLabelList.filter(
			(x): x is originTracingType => Object.values<string>(originTracingType).indexOf(x) < 0,
		);

		if (invalidParams.length > 0) {
			throw new Error(`Invalid Origin Tracing Parameter(s) supplied: ${invalidParams.join()}!`);
		}
		const isCaptureParam = (_: any, key: any) => originParamLabelList.indexOf(key) >= 0;

		// Capture params WITHOUT removing from URL (explicit no-op required because
		// urlParamExtractor defaults to calling history.replaceState if omitted)
		const paramExtractorReplace = () => {};
		const capturedURLParams: any = urlParamExtractor(isCaptureParam, paramExtractorReplace);

		// Apply provided handler function to extract origin trace ID from each URL parameter
		const applyHandlerFunction = (key: string): { [key: string]: any } => {
			const x = originParamHandlerMapping[key](capturedURLParams[key]);
			return { [key]: x };
		};
		const capturedOriginTraces = Object.keys(capturedURLParams)
			.map(applyHandlerFunction)
			.reduce((accumulator, currentValue) => Object.assign(accumulator, currentValue), {});

		if (Object.keys(capturedOriginTraces).length > 0) {
			this.pendingCleanup = () => {
				urlParamExtractor(isCaptureParam, historyReplaceFn);
			};
		}

		return capturedOriginTraces;
	};

	/** Executes and clears any pending origin param cleanup. No-op if nothing is pending. */
	public _cleanupOriginParams(): void {
		if (this.pendingCleanup) {
			const cleanup = this.pendingCleanup;
			this.pendingCleanup = null;
			cleanup();
		}
	}

	/**
	 * Designed to be invoked after a successful flush with the batch of flushed events.
	 */
	public cleanupOriginParameters(events: PackagedEvent[]): void {
		try {
			if (this.pendingCleanup) {
				const hasOriginLanded = events.some(
					(item) => 'event' in item.msg && item.msg.event === OriginTracing.messageEvent,
				);
				if (hasOriginLanded) {
					this._cleanupOriginParams();
				}
			}
		} catch {
			// Silently ignore cleanup errors to avoid disrupting the flush cycle
		}
	}
}
